import { useState } from 'react';
import PropTypes from 'prop-types';
import { UserPlus, Mail, User, Shield, Users } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { createUser } from '../../../services/userService';
import { useTeam } from '../../../context/TeamContext';
import './UserManagement.css';

/**
 * CreateUserModal Component
 * 
 * Modal for creating new users (admin only)
 */
export default function CreateUserModal({ isOpen, onClose, onSuccess }) {
  const { authorisedTeams, activeTeamId } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer',
    teamIds: activeTeamId ? [activeTeamId] : []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Team validation
    if (formData.teamIds.length === 0) {
      newErrors.teamIds = 'Select at least one team';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Call API to create user
      await createUser({
        email: formData.email,
        name: formData.name,
        role: formData.role,
        team_ids: formData.teamIds
      });

      // Show success notification
      if (onSuccess) {
        onSuccess('User created successfully');
      }

      // Reset form and close modal
      setFormData({ email: '', name: '', role: 'viewer', teamIds: activeTeamId ? [activeTeamId] : [] });
      setErrors({});
      onClose();
    } catch (error) {
      setErrors({
        submit: error.response?.data?.message || error.message || 'Failed to create user'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({ email: '', name: '', role: 'viewer', teamIds: activeTeamId ? [activeTeamId] : [] });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New User"
      size="md"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            icon={<UserPlus size={18} />}
          >
            Create User
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="user-form">
        {/* Email Field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            <Mail size={16} />
            Email Address
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="user@tui.co.uk"
            error={errors.email}
            disabled={loading}
            autoComplete="email"
          />
          {errors.email && (
            <span className="error-text">{errors.email}</span>
          )}
        </div>

        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            <User size={16} />
            Full Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            error={errors.name}
            disabled={loading}
            autoComplete="name"
          />
          {errors.name && (
            <span className="error-text">{errors.name}</span>
          )}
        </div>

        {/* Role Field */}
        <div className="form-group">
          <label htmlFor="role" className="form-label">
            <Shield size={16} />
            Role
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className={`form-select ${errors.role ? 'error' : ''}`}
            disabled={loading}
          >
            <option value="viewer">Viewer</option>
            <option value="manager">Report Manager</option>
            <option value="admin">Admin</option>
          </select>
          {errors.role && (
            <span className="error-text">{errors.role}</span>
          )}
          <p className="field-hint">
            Viewer: Read-only access | Report Manager: Can manage reports | Admin: Full access
          </p>
        </div>

        {/* Team Assignment */}
        <div className="form-group">
          <label className="form-label">
            <Users size={16} />
            Team Membership
          </label>
          <div className="folder-checkbox-list" style={{ maxHeight: '160px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--glass-bg)' }}>
            {realTeams.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>No teams available</span>}
            {realTeams.map(team => (
              <label key={team.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', cursor: 'pointer', padding: '0.25rem 0' }}>
                <input
                  type="checkbox"
                  checked={formData.teamIds.includes(team.id)}
                  onChange={() => {
                    setFormData(prev => ({
                      ...prev,
                      teamIds: prev.teamIds.includes(team.id)
                        ? prev.teamIds.filter(id => id !== team.id)
                        : [...prev.teamIds, team.id]
                    }));
                    if (errors.teamIds) setErrors(prev => ({ ...prev, teamIds: '' }));
                  }}
                  disabled={loading}
                />
                {team.name}
              </label>
            ))}
          </div>
          {errors.teamIds && <span className="error-text">{errors.teamIds}</span>}
          <p className="field-hint">Select one or more teams this user will have access to</p>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="form-error">
            {errors.submit}
          </div>
        )}
      </form>
    </Modal>
  );
}

CreateUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
